import React, { FunctionComponent, useEffect, useState } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import type { CreateTicketHandlers } from '../types';
import { useBuildTicketCreationRows } from './buildCreateTicketRows.helper';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useFetchPartnerAccounts from '@/hooks/useApi/useFetchPartnerAccounts';

const { Box, Button, Title, Table, TextField, Select, TextArea, InputFile, FilterableMultiSelector } = vp;

type TicketCreationProps = {
  handlers: CreateTicketHandlers;
  defaultValues: vp.SubmitTicketRequest;
  TicketCategories: vp.TicketCategory[];
  TicketSubCategories: vp.TicketSubCategory[];
  Users: vp.AccountUser[];
};

export type ItemProps = {
  label: string;
  value: string;
};
interface Users {
  Username: string;
  DateAdded: string;
}

type FC = FunctionComponent<TicketCreationProps>;
const TicketCreationForm: FC = (props: TicketCreationProps) => {
  const { handlers, defaultValues, TicketCategories, TicketSubCategories, Users } = props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const [partnerAccountsResponse, refreshPartnerAccounts] = useFetchPartnerAccounts({});
  const [userOptions, setUserOptions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Transform and filter partner accounts
  const allAccounts = React.useMemo(() => {
    return Object.entries(partnerAccountsResponse?.accountDetails || {}).reduce(
      (acc, [accountId, accountDetails]) => ({ ...acc, [accountId]: accountDetails.accountName }),
      {},
    );
  }, [partnerAccountsResponse]);

  useEffect(() => {
    refreshPartnerAccounts();
  }, []);

  const handleAccountSearch = (searchValue: string) => {
    refreshPartnerAccounts({ SearchText: searchValue });
  };

  const ticketRow = useBuildTicketCreationRows(
    allAccounts,
    TicketCategories,
    TicketSubCategories,
    userOptions,
    handleAccountSearch,
  );
  const { t } = useTranslation('translations', { keyPrefix: 'Tickets.Create' });

  useEffect(() => {
    setUserOptions(buildUserOptions(Object.values(Users || {})));
  }, [Users]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onCreateLead}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, setValues }) => {
        useEffect(() => {
          setValues({ ...values, TicketSubCategory: '' });
        }, [values.TicketCategory]);

        function fetchMapItems(item: {
          label: string;
          id: vp.CreateTicketId;
          require: boolean;
          visible: (ctx: vp.SubmitTicketRequest) => boolean;
          getSelectOptions?: (ctx: vp.SubmitTicketRequest) => { [key: string]: string };
          type: 'input' | 'select' | 'textarea' | 'inputfile' | 'multiSelector';
          preset: keyof vp.TextFieldPresets | undefined;
          getMultiSelectOptions?: (value: string) => ItemProps[];
          getMultiSelectValues?: (value: string) => ItemProps[];
          searchCallback?: (searchTerm: string) => void;
        }) {
          const {
            label,
            id,
            require,
            visible,
            getSelectOptions,
            type,
            preset,
            getMultiSelectOptions,
            getMultiSelectValues,
            searchCallback,
          } = item;

          return {
            label,
            id,
            require,
            visible,
            getSelectOptions,
            type,
            preset,
            getMultiSelectOptions,
            getMultiSelectValues,
            searchCallback,
          };
        }

        const ticketsTable: vp.TableProps = {
          name: 'add-user-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: ticketRow
            .filter((x) => x.visible(values))
            .map((item) => {
              const mapItem = fetchMapItems(item);

              return {
                Field: mapItem.label,
                Input:
                  mapItem.type === 'select' ? (
                    <Select
                      variant="standard"
                      options={mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : {}}
                      key={mapItem.id}
                      name={mapItem.id}
                      value={values[mapItem.id]}
                      placeholder=""
                      onBlur={handleBlur}
                      onChange={handleChange}
                      noOrdering
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                      searchCallback={mapItem.searchCallback}
                      disableFiltering={!!mapItem.searchCallback}
                    />
                  ) : mapItem.type === 'input' ? (
                    <TextField
                      preset={mapItem.preset}
                      key={mapItem.id}
                      name={mapItem.id}
                      variant="standard"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ) : mapItem.type === 'inputfile' ? (
                    <InputFile
                      id={mapItem.id}
                      key={mapItem.id}
                      name={mapItem.id}
                      variant="standard"
                      onBlur={handleBlur}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        handlers.updateFileName(event.target.attributes.getNamedItem('fileName')?.value ?? '');
                        handleChange(event);
                      }}
                      accept="image/jpeg,image/png,application/pdf"
                      validExtensions={['png', 'jpeg', 'jpg', 'pdf']}
                      maxSize={20}
                      label={t('fileUploadLabel')}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ) : mapItem.type === 'multiSelector' ? (
                    <FilterableMultiSelector
                      name={mapItem.id}
                      value={
                        mapItem.getMultiSelectValues ? mapItem.getMultiSelectValues(String(values[mapItem.id])) : []
                      }
                      items={mapItem.getMultiSelectOptions ? mapItem.getMultiSelectOptions(searchTerm) : []}
                      labelSize="16px"
                      indicatorMinHeight="40px"
                      height="42px"
                      minHeight="42px"
                      borderColor="#C4C4C4"
                      isNew={false}
                      setFieldValue={setFieldValue}
                      // useStateValues={false}
                      error={null}
                      nosort // do not sort long lists take for ever to render.
                      // onBlur={() => {}}
                      searchCallback={(_searchTeam: string) => {
                        setSearchTerm(_searchTeam);
                      }}
                    />
                  ) : (
                    <TextArea
                      key={mapItem.id}
                      name={mapItem.id}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      maxLength={5000}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ),
              };
            }),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Box mt="sm" mb="sm" jc="sb">
              <Title title={t('pageTitle')} />
            </Box>
            <Table {...ticketsTable} />

            <Box mt="md" jc="fe">
              <Button id="CreateTicket-submit" isDisabled={isRoleViewOnly} type="submit" label={t('submitButton')} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      TicketContent: Yup.string().required(),
      TicketSubject: Yup.string().required(),
      TicketCategory: Yup.string().required(),
      TicketSubCategory: Yup.string().required(),
    });
  }

  function buildUserOptions(lists: Users[]) {
    const options: { [key: string]: string } = {};

    lists.forEach((data) => {
      options[data.Username] = `${data.Username} `;
    });

    return options;
  }
};

export default TicketCreationForm;
